import type { FC } from 'react';
import { PageBreadcrumb, type PageBreadcrumbItem } from '@/components/shared/page-breadcrumb';
import { ROUTES } from '@/lib/constants/routes';

interface CampaignBreadcrumbProps {
  campaignId: string;
  campaignName: string;
  /** Optional last segment (e.g. "New audience"). When omitted, stops at campaign name. */
  current?: string;
}

/**
 * The `Campaigns / <campaign> [/ <current>]` trail — a convenience shape over
 * {@link PageBreadcrumb} so the four pages that show it don't each rebuild the same
 * two items. All styling comes from PageBreadcrumb; nothing is re-specified here.
 *
 * The name crumbs truncate and expose the full value on hover (NPJB-636); `Campaigns` and the
 * separators never do. A long campaign or audience name used to widen the row until the trail
 * ran past the page, since nothing here was allowed to shrink.
 */
export const CampaignBreadcrumb: FC<CampaignBreadcrumbProps> = ({
  campaignId,
  campaignName,
  current,
}) => {
  // The campaign crumb is always a link: on every page that shows this trail the
  // campaign is an ancestor, never the page you are on. Callers that omit `current`
  // (audience overview / edit) simply don't contribute a crumb of their own, so the
  // trail ends on an ancestor — passing `current` is preferred where a label exists.
  const items: PageBreadcrumbItem[] = [
    { label: 'Campaigns', to: ROUTES.campaigns },
    { label: campaignName, to: `/campaigns/${campaignId}`, truncate: true },
  ];
  if (current !== undefined) items.push({ label: current, truncate: true });

  return <PageBreadcrumb items={items} />;
};
